/* eslint-disable jsx-a11y/label-has-associated-control */
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Formik, Field, Form, type FormikHelpers } from 'formik';
import { CCard, CCardHeader, CCardBody, CCol, CFormFeedback } from '@coreui/react';
import * as yup from 'yup';
import TextInput from 'components/inputs/TextInput';
import Button from 'components/common/Button';
import Editor from 'components/common/Editor';
import { useIntl } from 'react-intl';
import { routePatterns } from 'router/route-paths';
import { hasText } from 'components/common/Editor.utils';
import { translatedMessage } from 'utils/yup';
import s from './Feedback.module.scss';

export interface IFeedbackFormValues {
  name: string;
  email: string;
  message: string;
}

interface IFeedbackFormProps {
  initialValues: IFeedbackFormValues;
  onSubmit: (
    values: IFeedbackFormValues,
    formikActions: FormikHelpers<IFeedbackFormValues>,
  ) => Promise<void>;
  message: ReactNode;
}

/** An empty answer would send an empty email. */
const validationSchema = yup.object().shape({
  message: yup.string().test('has-text', translatedMessage('messages.answer_required'), hasText),
});

const FeedbackForm = ({ initialValues, onSubmit, message }: IFeedbackFormProps) => {
  const { formatMessage: t } = useIntl();
  return (
    <div>
      <Formik {...{ initialValues, onSubmit, validationSchema }}>
        {({ isSubmitting, touched, errors }) => (
          <CCol xs={12} md={9} lg={7} className="mx-auto">
            <CCard>
              <CCardHeader>{t({ id: 'messages.feedback' })}</CCardHeader>
              <CCardBody>
                <Form>
                  <Field
                    name="email"
                    component={TextInput}
                    required
                    max={100}
                    label={t({ id: 'auth.email' })}
                    disabled
                    invalid={!!(touched.email && errors.email)}
                  />
                  <Field
                    name="name"
                    component={TextInput}
                    required
                    max={100}
                    label={t({ id: 'auth.name' })}
                    disabled
                    invalid={!!(touched.name && errors.name)}
                  />
                  <label>{t({ id: 'messages.userMessage' })}</label>
                  <p className={s.message}>{message}</p>
                  <Field name="message" component={Editor} />
                  {touched.message && errors.message && (
                    <CFormFeedback invalid className="help-block d-block">
                      {errors.message}
                    </CFormFeedback>
                  )}
                  <div className={s.buttonsWrapper}>
                    <Link to={routePatterns.messages.stringify({ page: 1 })}>
                      <Button color="transparent" type="submit" disabled={isSubmitting}>
                        {t({ id: 'buttons.cancel' })}
                      </Button>
                    </Link>
                    <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                      {t({ id: 'app.send' })}
                    </Button>
                  </div>
                </Form>
              </CCardBody>
            </CCard>
          </CCol>
        )}
      </Formik>
    </div>
  );
};

export default FeedbackForm;
